import {
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsEmail,
  IsIn,
  IsISO8601,
  IsOptional,
  IsString,
  IsUUID,
  MaxLength,
  MinLength,
} from 'class-validator';
import {
  COMPANY_ROLES,
  CONTRACT_STATUSES,
  FILE_ROLES,
  APPOINTMENT_AUTHORITIES,
  GOVERNMENT_MEMBERSHIP_ROLES,
  SIGNABLE_KINDS,
  type AppointmentAuthority,
  type CompanyRole,
  type ContractStatus,
  type FileRole,
  type GovernmentMembershipRole,
  type GovernmentRole,
  type Permission,
  type SignableKind,
} from '@munifocus/access';

/**
 * The Government Portal's administration surface: companies, contracts,
 * appointments, municipal seats, file teams and signing authority.
 *
 * Everything here is scoped. A request that does not name a municipality, a
 * company or a file is a request the API cannot authorize, so the ids are
 * required rather than inferred from the session — a person may hold seats at
 * several municipalities, and guessing which one they meant is how a file gets
 * created in the wrong tenant.
 */

// ── Companies ──────────────────────────────────────────────────────────────

export const COMPANY_STATUSES = ['active', 'suspended', 'closed'] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export interface CompanySummary {
  id: string;
  legalName: string;
  operatingName: string;
  officialNameEn: string;
  officialNameFr: string;
  email: string;
  phone: string;
  status: CompanyStatus;
  /** Live contracts, for the "contracted at" column. */
  activeContractCount: number;
  employeeCount: number;
  /** Enforcement seats given out, and held — "N of M seats". */
  seatsUsed: number;
  seatLimit: number;
  createdAt: string;
}

export class CreateCompanyDto {
  @IsString()
  @MaxLength(300)
  legalName!: string;

  @IsOptional() @IsString() @MaxLength(300) operatingName?: string;
  @IsOptional() @IsString() @MaxLength(300) officialNameEn?: string;
  @IsOptional() @IsString() @MaxLength(300) officialNameFr?: string;
  @IsOptional() @IsString() @MaxLength(400) streetAddress?: string;
  @IsOptional() @IsString() @MaxLength(16) postalCode?: string;
  @IsOptional() @IsString() @MaxLength(100) province?: string;
  @IsOptional() @IsString() @MaxLength(40) phone?: string;
  @IsOptional() @IsEmail() email?: string;
  @IsOptional() @IsString() notes?: string;
}

export class UpdateCompanyDto extends CreateCompanyDto {
  @IsOptional()
  @IsIn(COMPANY_STATUSES)
  status?: CompanyStatus;
}

// ── Contracts ──────────────────────────────────────────────────────────────

export interface ContractSummary {
  id: string;
  companyId: string;
  companyName: string;
  localGovernmentId: string;
  localGovernmentName: string;
  reference: string;
  /** Folds `expired` in from the dates — see `effectiveContractStatus`. */
  status: ContractStatus;
  startsOn: string | null;
  endsOn: string | null;
  councilResolutionRef: string;
  signedByName: string | null;
  signedAt: string | null;
  /** Officers currently appointed under this contract. */
  appointmentCount: number;
  notes: string;
  /**
   * Filename of the executed contract PDF, or null when none was uploaded.
   *
   * The name and not the storage key: the key is an S3 path the browser cannot
   * use and should not learn, and the only questions a screen asks are "is there
   * a signed copy" and "what is it called". The bytes come from
   * `GET /administration/contracts/:id/document`, which mints a short-lived
   * signed URL per read.
   */
  documentFilename: string | null;
  /**
   * The municipality's reason the last time it turned this contract down, or
   * null. Stays on a resubmitted draft so the company can see what to change.
   */
  rejectionReason: string | null;
}

export class CreateContractDto {
  @IsUUID()
  companyId!: string;

  @IsUUID()
  localGovernmentId!: string;

  @IsOptional() @IsString() @MaxLength(64) reference?: string;
  @IsOptional() @IsISO8601() startsOn?: string;
  @IsOptional() @IsISO8601() endsOn?: string;

  @IsOptional()
  @IsArray()
  @IsUUID(undefined, { each: true })
  scopeLawIds?: string[];

  @IsOptional() @IsString() notes?: string;
}

export class UpdateContractDto {
  /**
   * Accepted for older clients and ignored.
   *
   * The API scopes every contract-by-id route to the municipality the contract
   * row belongs to (`ScopeFrom.tenantRecord('contract')`). It used to read this
   * field instead, which let a caller name their own municipality and edit
   * another's contract. A contract cannot be moved between municipalities, so
   * the value is never written either.
   */
  @IsOptional()
  @IsUUID()
  localGovernmentId?: string;

  @IsOptional() @IsString() @MaxLength(64) reference?: string;
  @IsOptional() @IsISO8601() startsOn?: string;
  @IsOptional() @IsISO8601() endsOn?: string;

  /**
   * Only meaningful before the contract is signed — the signing act sets it, and
   * this is how a typo in it gets corrected without re-signing.
   */
  @IsOptional() @IsString() @MaxLength(120) councilResolutionRef?: string;

  @IsOptional()
  @IsArray()
  @IsUUID(undefined, { each: true })
  scopeLawIds?: string[];

  @IsOptional() @IsString() notes?: string;
}

/**
 * Make a contract live.
 *
 * The resolution reference is required, not optional: a municipality is bound by
 * a council resolution, not by whoever happened to be logged in. `signedByUserId`
 * records the person who entered it; this records the authority they entered it
 * under, and without it the signature has nothing behind it.
 */
export class SignContractDto {
  @IsString()
  @MaxLength(120)
  councilResolutionRef!: string;

  @IsOptional() @IsISO8601() startsOn?: string;
  @IsOptional() @IsISO8601() endsOn?: string;
}

/**
 * The municipality turning down a contract or a proposed officer.
 *
 * The reason is required: it is sent to the company, and a refusal without one
 * leaves them guessing what to change before proposing again.
 */
export class RejectProposalDto {
  @IsString()
  @MinLength(3)
  @MaxLength(2000)
  reason!: string;
}

// ── Revocation and succession ──────────────────────────────────────────────

/** One file blocking a revocation, with the successor the API proposes. */
export interface SuccessionCandidate {
  fileId: string;
  fileReference: string;
  localGovernmentId: string;
  /** Why this file blocks: the person being revoked leads it. */
  currentLeadUserId: string;
  currentLeadName: string;
  /** Null when nobody can be found — the revocation cannot proceed. */
  proposedSuccessorUserId: string | null;
  proposedSuccessorName: string | null;
  /**
   * How the successor was chosen, so the confirming screen can explain itself
   * rather than presenting a name with no reasoning.
   */
  basis: 'assisting_officer' | 'appointment_supervisor' | 'municipal_supervisor' | 'none';
}

/**
 * What a revocation would do, returned before anything is written.
 *
 * The brief's rule — "transfer to another officer; if nobody else, supervisor is
 * the officer" — is enforced as a refusal: `blocked` is true when any open file
 * has no possible successor, and the revocation is rejected rather than leaving
 * a live legal record unowned.
 */
export interface RevocationPlan {
  /** True when every open file has a successor and the revocation may proceed. */
  canProceed: boolean;
  files: SuccessionCandidate[];
  /** Files with `basis: 'none'` — the reason a revocation cannot proceed. */
  unresolvableCount: number;
}

/** Confirm a revocation, optionally overriding the proposed successors. */
export class ConfirmRevocationDto {
  @IsOptional()
  @IsArray()
  overrides?: Array<{ fileId: string; successorUserId: string }>;

  @IsOptional() @IsString() reason?: string;
}

// ── Officer appointments ───────────────────────────────────────────────────

/**
 * Re-exported from `@munifocus/access`, which owns the one definition — the
 * Government Portal and the admin portal must not be able to disagree about
 * which statutes an appointment confers authority under.
 */
export { APPOINTMENT_AUTHORITIES, type AppointmentAuthority };

export interface AppointmentSummary {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  localGovernmentId: string;
  localGovernmentName: string;
  /** Null for a municipality's own employee. */
  companyContractId: string | null;
  companyName: string | null;
  status: 'pending_council' | 'active' | 'expired' | 'revoked' | 'rejected';
  /** Why the municipality turned the proposal down, when it did. */
  rejectionReason: string | null;
  authorities: AppointmentAuthority[];
  badgeNumber: string;
  appointedOn: string | null;
  expiresOn: string | null;
  supervisorUserId: string | null;
  supervisorName: string | null;
  councilResolutionRef: string;
  openFileCount: number;
}

/**
 * Propose or make an appointment.
 *
 * A company admin holding `officer:propose` creates this in `pending_council`;
 * the clerk holding `officer:appoint` creates it — or approves it — as `active`.
 * The API decides which from the caller's permissions rather than trusting a
 * field in the body.
 *
 * Nobody on council holds an account, so an `active` appointment is always the
 * clerk recording a resolution. `councilResolutionRef` is where that resolution
 * goes; it is optional here pending the client's answer on whether an
 * appointment without one should be refused outright.
 */
export class CreateAppointmentDto {
  @IsUUID()
  userId!: string;

  @IsUUID()
  localGovernmentId!: string;

  /** Omit for a municipality's own employee. */
  @IsOptional()
  @IsUUID()
  companyContractId?: string;

  @IsArray()
  @ArrayNotEmpty()
  @IsIn(APPOINTMENT_AUTHORITIES, { each: true })
  authorities!: AppointmentAuthority[];

  @IsOptional() @IsString() @MaxLength(40) badgeNumber?: string;
  @IsOptional() @IsISO8601() appointedOn?: string;
  @IsOptional() @IsISO8601() expiresOn?: string;

  /** Default successor if this appointment is ever revoked. */
  @IsOptional()
  @IsUUID()
  supervisorUserId?: string;

  /** The council resolution authorising the appointment, e.g. "2026-041". */
  @IsOptional() @IsString() @MaxLength(120) councilResolutionRef?: string;

  @IsOptional() @IsString() notes?: string;
}

// ── Municipal seats ────────────────────────────────────────────────────────

export interface GovernmentMemberSummary {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  localGovernmentId: string;
  role: GovernmentMembershipRole;
  title: string;
  appointedOn: string | null;
  expiresOn: string | null;
  isActive: boolean;
}

export class CreateGovernmentMemberDto {
  /** Existing account, or omit and supply `email` + `displayName` to invite. */
  @IsOptional()
  @IsUUID()
  userId?: string;

  @IsOptional() @IsEmail() email?: string;
  @IsOptional() @IsString() @MaxLength(200) displayName?: string;
  /** An invited account's language, for its mail and first sign-in. Ignored for an existing account. */
  @IsOptional() @IsIn(['en', 'fr']) language?: 'en' | 'fr';

  @IsUUID()
  localGovernmentId!: string;

  @IsIn(GOVERNMENT_MEMBERSHIP_ROLES)
  role!: GovernmentMembershipRole;

  @IsOptional() @IsString() @MaxLength(200) title?: string;
  @IsOptional() @IsISO8601() appointedOn?: string;
  @IsOptional() @IsISO8601() expiresOn?: string;
}

// ── A contractor's local-government contacts ───────────────────────────────

/**
 * The seats a contractor may hand out at a municipality on the limited
 * licence: the clerk who approves its contract and officers, the solicitor,
 * and read-only observers. Supervisor, finance and staff seats are the
 * municipality's own business.
 */
export const LG_CONTACT_ROLES = ['clerk', 'counsel', 'gov_observer'] as const;
export type LgContactRole = (typeof LG_CONTACT_ROLES)[number];

/** One municipality a contractor serves on the limited licence, and who is seated there. */
export interface LgContactsSummary {
  localGovernmentId: string;
  localGovernmentName: string;
  members: GovernmentMemberSummary[];
  /**
   * The memberships this company added, and so may remove. The town's own
   * clerk and anyone Munifocus seated are not the company's to take away.
   */
  removableIds: string[];
}

/** A contractor inviting someone at a municipality it serves. */
export class CreateLgContactDto {
  @IsUUID()
  localGovernmentId!: string;

  @IsEmail()
  email!: string;

  @IsOptional() @IsString() @MaxLength(200) displayName?: string;
  /** An invited account's language, for its mail and first sign-in. Ignored for an existing account. */
  @IsOptional() @IsIn(['en', 'fr']) language?: 'en' | 'fr';

  @IsIn(LG_CONTACT_ROLES)
  role!: LgContactRole;
}

// ── Company employees ──────────────────────────────────────────────────────

export interface CompanyMemberSummary {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  companyId: string;
  role: CompanyRole;
  title: string;
  isActive: boolean;
  /** Municipalities this employee currently holds an appointment at. */
  appointedAt: Array<{ localGovernmentId: string; name: string }>;
  /** Whether this employee holds one of the company's enforcement seats. */
  hasSeat: boolean;
  seatAssignedAt: string | null;
}

export class CreateCompanyMemberDto {
  @IsOptional() @IsUUID() userId?: string;
  @IsOptional() @IsEmail() email?: string;
  @IsOptional() @IsString() @MaxLength(200) displayName?: string;
  /** An invited account's language, for its mail and first sign-in. Ignored for an existing account. */
  @IsOptional() @IsIn(['en', 'fr']) language?: 'en' | 'fr';

  @IsIn(COMPANY_ROLES)
  role!: CompanyRole;

  @IsOptional() @IsString() @MaxLength(200) title?: string;
}

export class UpdateCompanyMemberDto {
  @IsOptional() @IsIn(COMPANY_ROLES) role?: CompanyRole;
  @IsOptional() @IsString() @MaxLength(200) title?: string;
  @IsOptional() @IsBoolean() isActive?: boolean;
}

// ── File teams ─────────────────────────────────────────────────────────────

export interface FileTeamMember {
  id: string;
  fileId: string;
  userId: string;
  userName: string;
  userEmail: string;
  role: FileRole;
  grantedAt: string;
  grantedByName: string | null;
}

export class AddFileTeamMemberDto {
  @IsUUID()
  userId!: string;

  @IsIn(FILE_ROLES)
  role!: FileRole;
}

// ── Signing authority ──────────────────────────────────────────────────────

/**
 * One person who could be granted or denied signing authority.
 *
 * Seats and appointments both, because both sign: a clerk holds a
 * `government_membership`, an officer holds an `officer_appointment`, and a
 * picker that reads only the first is empty at every municipality served by a
 * contractor. `role` disambiguates the same person appearing twice — holding
 * two seats at one municipality is legal and happens.
 */
export interface SigningCandidate {
  userId: string;
  userName: string;
  userEmail: string;
  role: GovernmentRole;
  /** Their seat title, or an officer's badge number. Empty when neither. */
  title: string;
}

export interface SigningAuthorityRow {
  userId: string;
  userName: string;
  localGovernmentId: string;
  documentKind: SignableKind;
  /** True when this comes from the role default rather than an explicit row. */
  fromRoleDefault: boolean;
  effect: 'allow' | 'deny';
  grantedByName: string | null;
  grantedAt: string | null;
}

export class GrantSigningAuthorityDto {
  @IsUUID()
  userId!: string;

  @IsIn(SIGNABLE_KINDS)
  documentKind!: SignableKind;

  @IsIn(['allow', 'deny'])
  effect!: 'allow' | 'deny';

  @IsOptional() @IsString() notes?: string;
}

// ── Session shape ──────────────────────────────────────────────────────────

/** One municipality or company the signed-in account can act in. */
export interface AccessibleScope {
  kind: 'local_government' | 'company';
  id: string;
  name: string;
  /** The roles held here, for the scope switcher's subtitle. */
  roles: string[];
  /**
   * The company this municipality is reachable *through*, when that is the only
   * reason it is offered.
   *
   * A contractor's employee gets a scope for every municipality their company
   * holds a live contract with, so the switcher lists places the person has no
   * direct relationship with. Without saying where those came from the list
   * reads as a flat set of unrelated employers, and the honest question — "am I
   * here as myself or as MES?" — has no answer on screen.
   *
   * Null when the account holds the place in its own right: a seat or an
   * appointment outranks the contract edge, and a clerk of Moncton is not
   * "Moncton via" anything. Always null for `kind: 'company'`.
   */
  via?: { id: string; name: string } | null;
}

/**
 * What the Government Portal shell needs to render itself: where the account can
 * work, and what it may do in the scope it currently has selected.
 *
 * Sent as one payload rather than derived client-side from a role string,
 * because the client must never be the thing that decides what is permitted —
 * it decides only what to *show*, and the API refuses anything it gets wrong.
 */
export interface SessionAccess {
  scopes: AccessibleScope[];
  /** Permissions in the active scope, for gating navigation and buttons. */
  permissions: Permission[];
  /** True when the account holds seats in more than one place. */
  needsScopeSwitcher: boolean;
}

/**
 * Where a scope is on the map, for the field map's opening view when there are
 * no pinned files to fit to.
 *
 * Null when the place could not be located — a company (which works somewhere,
 * but is not itself a place), or a geocoder that did not answer. The portal then
 * falls back to the whole province rather than to somebody else's town.
 */
export interface ScopeCentre {
  centre: { latitude: number; longitude: number } | null;
}

export { CONTRACT_STATUSES };
export type {
  ContractStatus,
  GovernmentMembershipRole,
  GovernmentRole,
  CompanyRole,
  FileRole,
  SignableKind,
};

/**
 * Somebody a municipality could appoint as an officer.
 *
 * `kind` is what the picker groups by; `source` is the employer's own name and
 * is empty for municipal staff, who are named by the client in the reader's
 * language. The API used to send a hard-coded English "Municipal staff" here,
 * which reached a francophone clerk untranslated.
 */
export interface AppointableCandidate {
  id: string;
  displayName: string;
  email: string;
  kind: 'municipal' | 'company';
  source: string;
}
